import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';

const ALL_MODULES = [
  'hero', 'experiences', 'reservasSedes', 'reservations', 'eventBookings',
  'menuRecommendations', 'menuSedes', 'menuManagement', 'dishManagement',
  'eventSpaces', 'promotionsConfig', 'promotionBookings', 'socialLinks',
  'reviews', 'reservationsManagement', 'userManagement'
];

function AdminUserManagement() {
  const [users, setUsers] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [message, setMessage] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserPermissions, setEditingUserPermissions] = useState({});

  const usersCollectionRef = collection(db, 'users');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Users
        const usersData = await getDocs(usersCollectionRef);
        setUsers(usersData.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      } catch (error) {
        console.error('Error fetching data:', error);
        setMessage('Error al cargar usuarios.');
      }
    };
    fetchData();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      // Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, newUserEmail, newUserPassword);
      const user = userCredential.user;

      // Add user document to Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: newUserEmail,
        role: 'user',
        permissions: {},
        createdAt: new Date(),
      });
      // After creating the new user, sign out the newly created user
      // to prevent the super admin from being logged out of their session.
      // For a more robust solution, consider using Firebase Cloud Functions
      // to create users, as the Admin SDK does not automatically sign in the user.
      await auth.signOut();

      setMessage('Usuario añadido con éxito! Por favor, recargue la página o inicie sesión de nuevo si las secciones del dashboard no se actualizan.');
      setNewUserEmail('');
      setNewUserPassword('');
      // Refresh user list
      const usersData = await getDocs(usersCollectionRef);
      setUsers(usersData.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      console.error('Error adding user:', error);
      setMessage(`Error al añadir usuario: ${error.message}`);
    }
  };

  const handleEditUserPermissions = (userId, currentPermissions) => {
    setEditingUserId(userId);
    setEditingUserPermissions(currentPermissions || {});
  };

  const handleSaveUserPermissions = async (userId) => {
    setMessage('');
    try {
      const userDoc = doc(db, 'users', userId);
      await updateDoc(userDoc, {
        permissions: editingUserPermissions,
      });
      setMessage('Permisos de usuario actualizados con éxito!');
      setEditingUserId(null);
      setEditingUserPermissions({});
      // Refresh user list
      const usersData = await getDocs(usersCollectionRef);
      setUsers(usersData.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    } catch (error) {
      console.error('Error updating user permissions:', error);
      setMessage(`Error al actualizar permisos: ${error.message}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este usuario? Esto no eliminará la cuenta de autenticación de Firebase.')) {
      setMessage('');
      try {
        await deleteDoc(doc(db, 'users', userId));
        setMessage('Usuario eliminado con éxito!');
        // Refresh user list
        const usersData = await getDocs(usersCollectionRef);
        setUsers(usersData.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      } catch (error) {
        console.error('Error deleting user:', error);
        setMessage(`Error al eliminar usuario: ${error.message}`);
      }
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h3>Gestión de Usuarios Administradores</h3>
      
      {message && <p style={{ color: message.includes('Error') ? 'red' : 'green' }}>{message}</p>}

      <h4>Añadir Nuevo Usuario</h4>
      <form onSubmit={handleAddUser} style={{ marginBottom: '20px' }}>
        <input
          type="email"
          placeholder="Email"
          value={newUserEmail}
          onChange={(e) => setNewUserEmail(e.target.value)}
          required
          style={{ marginRight: '10px', padding: '8px' }}
        />
        <input
          type="password"
          placeholder="Contraseña"
          value={newUserPassword}
          onChange={(e) => setNewUserPassword(e.target.value)}
          required
          style={{ marginRight: '10px', padding: '8px' }}
        />
        <button type="submit" style={{ padding: '8px 12px' }}>Añadir Usuario</button>
      </form>

      <h4>Usuarios Existentes</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Email</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Rol</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Permisos</th>
            <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{user.email}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{user.role}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                {editingUserId === user.id ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {ALL_MODULES.map(module => (
                      <label key={module} style={{ display: 'flex', alignItems: 'center', fontSize: '0.8em' }}>
                        <input
                          type="checkbox"
                          checked={editingUserPermissions[module] || false}
                          onChange={(e) => setEditingUserPermissions(prev => ({ ...prev, [module]: e.target.checked }))}
                          style={{ marginRight: '3px' }}
                        />
                        {module}
                      </label>
                    ))}
                  </div>
                ) : (
                  Object.keys(user.permissions || {}).filter(perm => user.permissions[perm]).join(', ') || 'Ninguno'
                )}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                {editingUserId === user.id ? (
                  <button onClick={() => handleSaveUserPermissions(user.id)} style={{ marginRight: '5px' }}>Guardar</button>
                ) : (
                  <button onClick={() => handleEditUserPermissions(user.id, user.permissions)} style={{ marginRight: '5px' }}>Editar Permisos</button>
                )}
                <button onClick={() => handleDeleteUser(user.id)}>Eliminar</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminUserManagement;
